/**
 * Host auth gate (#171, FR-12.7) — hosting a game requires a registered account.
 *
 * Wraps the create-game surface: when registered auth is enabled and the caller is a
 * guest (or has no identity yet), it presents the existing app-owned {@link AuthPanel}
 * behind a "Sign in to host" prompt instead of the create flow. The panel's full-page
 * resume returns to `/host` (the stashed `returnTo`), where the now-registered caller
 * falls through to the wrapped children. Players and join flows are never gated.
 *
 * Degrade-aware (D171.2 / D-43.9): when Cognito is not configured (`enabled === false` —
 * local dev / a guest-only deployment) the gate is transparent and renders its children,
 * mirroring the server guard which is a no-op without `authConfig`. The server remains
 * authoritative; this only spares the guest a doomed create round trip.
 */

import { useAuth } from '../net/use-auth';
import { AuthPanel } from './auth/AuthPanel';

export interface HostGateProps {
  /** The create-game surface, rendered once hosting is permitted. */
  children: React.ReactNode;
}

export function HostGate({ children }: HostGateProps): React.JSX.Element {
  const auth = useAuth();
  // Only block when auth is actually enabled AND the caller is not a registered account.
  // While the identity is still resolving (null) under enabled auth, treat as must-sign-in
  // so we never flash the create flow to a guest.
  const mustSignIn = auth.enabled && (auth.identity === null || auth.identity.isGuest);

  if (!mustSignIn) return <>{children}</>;

  return (
    <div className="host-gate" data-testid="host-gate">
      {auth.open ? (
        <AuthPanel auth={auth} />
      ) : (
        <div className="host-gate__prompt stack">
          <h2 className="create-game__title">Sign in to host</h2>
          <p className="create-game__subtitle muted">
            Hosting a game needs a free account — you get one free game to start. Players
            can join without an account.
          </p>
          <button
            type="button"
            className="btn btn--primary"
            data-testid="host-gate-signin"
            onClick={() => auth.openPanel('signUp')}
          >
            Create a free account
          </button>
        </div>
      )}
    </div>
  );
}
